import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'
import React, { useEffect } from 'react'
import { useTheme } from 'styled-components'
import { useNavigate } from 'react-router'

import { useStore } from '../../../application/store/store'
import { usePlantInfosUseCase } from '../../../application/useCases/usePlantInfos'
import environnement from '../../../infrastructure/config/environnement'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import PlantSearchRedirect from '../../components/plantSearchRedirect/plantSearchRedirect'
import { PlantSearchSelected } from '../../components/plantSearchRedirect/plantSearchSelected'
import {
  ButtonBase,
  Card,
  CardContent,
  CardFooter,
  CardTitle,
  CenterWrapper,
  ColumnWrapper,
  ContentWrapper,
  DescriptionInfo,
  DescriptionInfos,
  Header,
  ImgWrapper,
  PageWrapper,
  TextData,
  Wrapper,
} from './plantInfos.styles'

type Props = {
  name: string
  data?: string | number | null
}

export const PlantDescriptionCard = (props: Props): React.ReactElement => {
  const { name, data } = props
  const dataToPrint = typeof data === 'string' ? data : data?.toString()
  return (
    <DescriptionInfo>
      <div>{name}</div>
      <div>{dataToPrint || 'N/A'}</div>
    </DescriptionInfo>
  )
}

const PlantInfos: React.FC = () => {
  const navigate = useNavigate()
  const {
    setChargeExploitationData,
    cleanChargeExploitationData,
    plantInfosLoading,
    chargeExploitationData,
    selectedPlant,
  } = useStore()
  const { getChargeExploitation } = usePlantInfosUseCase()

  const { isMobile } = useTheme()
  const pictureUrl = `${environnement.FILES_URL}${chargeExploitationData?.photo}`
  const { t } = useCustomTranslation('common')

  const centraleType = selectedPlant?.technos?.map((techno) => techno.libelle).join(', ')

  useEffect(() => {
    const fetchChargeExploitationData = async () => {
      if (selectedPlant) {
        if (selectedPlant.mail_charge_exploitation) {
          const chargeExploitationData = await getChargeExploitation(
            selectedPlant.mail_charge_exploitation
          )
          setChargeExploitationData(chargeExploitationData)
        } else {
          cleanChargeExploitationData()
        }
      } else {
        navigate('/plant-select')
      }
    }
    fetchChargeExploitationData()
  }, [selectedPlant])

  return (
    <PageWrapper>
      {!isMobile && (
        <>
          <PlantSearchRedirect />
          <Header>
            <PlantSearchSelected selectedPlant={selectedPlant} title={t('administration')} />
          </Header>
        </>
      )}
      {plantInfosLoading ? (
        <CenterWrapper>
          <FontAwesomeIcon icon="spinner" spin size="lg" />
          <p>{`${t('collect_your_plant_data')}...`}</p>
        </CenterWrapper>
      ) : (
        <ContentWrapper>
          <Wrapper flex="2">
            <Card>
              <CardTitle>{t('plant_description')}</CardTitle>
              <DescriptionInfos>
                <Wrapper>
                  <PlantDescriptionCard name="ID" data={selectedPlant?.code_centrale} />
                  <PlantDescriptionCard name="Nom" data={selectedPlant?.nom} />
                  <PlantDescriptionCard name="Type" data={centraleType} />
                  <PlantDescriptionCard
                    name="Puissance (kWc)"
                    data={selectedPlant?.puissance_crete}
                  />
                  {selectedPlant?.date_msi && (
                    <PlantDescriptionCard
                      name="Mise en service"
                      data={format(new Date(selectedPlant.date_msi), 'dd/MM/yyyy')}
                    />
                  )}
                </Wrapper>
                <Wrapper>
                  <PlantDescriptionCard name="Adresse" data={selectedPlant?.voie} />
                </Wrapper>
                <Wrapper>
                  <PlantDescriptionCard name="Code postal" data={selectedPlant?.code_postal} />
                  <PlantDescriptionCard name="Ville" data={selectedPlant?.commune} />
                  {selectedPlant?.pays && (
                    <PlantDescriptionCard name="Pays" data={selectedPlant.pays} />
                  )}
                </Wrapper>
              </DescriptionInfos>
            </Card>
          </Wrapper>
          {chargeExploitationData && (
            <Wrapper>
              <Card>
                <CardTitle>{t('my_urbasolar_contact')}</CardTitle>
                <CardContent>
                  <ColumnWrapper style={{ justifyContent: 'center' }}>
                    <TextData size={isMobile ? 'h4' : 'h2'}>
                      {chargeExploitationData.user?.prenom}{' '}
                      {chargeExploitationData.user?.nom.toUpperCase()}
                    </TextData>
                    <TextData size={isMobile ? 'h5' : 'h3'} fontWeight={700}>
                      {chargeExploitationData.poste}
                    </TextData>
                    <TextData size={isMobile ? 'h6' : 'h4'}>
                      {chargeExploitationData.user.telephone}
                    </TextData>
                    <TextData size={isMobile ? 'h6' : 'h4'}>
                      {chargeExploitationData.mail_user}
                    </TextData>
                  </ColumnWrapper>
                  {chargeExploitationData.photo && !isMobile && (
                    <Wrapper style={{ alignItems: 'center', justifyContent: 'center' }}>
                      <ImgWrapper profilepic={pictureUrl} />
                    </Wrapper>
                  )}
                </CardContent>
                <CardFooter>
                  <ButtonBase href={`mailto:${chargeExploitationData.mail_user}`}>
                    <FontAwesomeIcon icon="envelope" size="2x" />
                  </ButtonBase>
                </CardFooter>
              </Card>
            </Wrapper>
          )}
        </ContentWrapper>
      )}
    </PageWrapper>
  )
}

export default PlantInfos
